/**
 * PlanningContext — Provider only. Helpers, hook, and types live in
 * `./planningContextCore` so this .tsx file exports only a component
 * (react-refresh/only-export-components).
 */
import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import type { ContentBrief, ContentBriefPlan } from "@/lib/composerCore";
import { clearApprovedBriefPlan } from "@/lib/composerCore";
import {
  PlanningCtx,
  type ApprovedBrief,
  type LiveBrief,
  type PlanningContextValue,
} from "./planningContextCore";

type ProviderProps = {
  workspaceId: string | null;
  brief: ContentBrief | null;
  latestPlan: ContentBriefPlan | null;
  onRefresh: () => Promise<void> | void;
  children: ReactNode;
};

export const PlanningProvider = ({
  workspaceId,
  brief,
  latestPlan,
  onRefresh,
  children,
}: ProviderProps) => {
  const [liveBrief, setLiveBrief] = useState<LiveBrief>(null);
  const approvedJson =
    (brief as { approved_brief_json?: ApprovedBrief | null } | null)?.approved_brief_json ?? null;
  const isApprovedRef = useRef<boolean>(Boolean(approvedJson) && brief?.status === "approved");
  isApprovedRef.current = Boolean(approvedJson) && brief?.status === "approved";

  const handleBeforeMutateApproved = useCallback(async () => {
    if (!isApprovedRef.current) return true;
    if (!workspaceId || !brief) return true;
    const ok = window.confirm(
      "Editing or regenerating will invalidate the currently approved brief. Continue?",
    );
    if (!ok) return false;
    try {
      await clearApprovedBriefPlan(workspaceId, brief.id);
      await onRefresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to clear approved brief");
      return false;
    }
    return true;
  }, [workspaceId, brief, onRefresh]);

  const value = useMemo<PlanningContextValue>(
    () => ({
      workspaceId,
      brief,
      latestPlan,
      onRefresh,
      liveBrief,
      setLiveBrief,
      handleBeforeMutateApproved,
    }),
    [workspaceId, brief, latestPlan, onRefresh, liveBrief, handleBeforeMutateApproved],
  );

  return <PlanningCtx.Provider value={value}>{children}</PlanningCtx.Provider>;
};
